import {
  ApolloError,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
  useQuery,
} from '@apollo/client'
import { DocumentNode } from 'graphql'
import { useContext } from 'react'

import { TokenContext } from '../layouts/context'
import { useCustomerId } from './useCustomerId'

export type CamelQueryResult<TData, TVariables> = Omit<QueryResult<TData, TVariables>, 'error'> & {
  error?: ApolloError | Error
}

type OmitProvided<TVariables> = Omit<TVariables, 'customerId'>

export type UseCamelQuery<TDocumentNode> =
  TDocumentNode extends TypedDocumentNode<infer TData, infer TVariables>
    ? CamelQueryResult<TData, OmitProvided<TVariables>>
    : never

const useCamelQuery = <TData = unknown, TVariables = Record<string, unknown>>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, OmitProvided<TVariables>>,
): CamelQueryResult<TData, OmitProvided<TVariables>> => {
  const accessToken = useContext(TokenContext)
  const customerId = useCustomerId()
  const useQueryResult = useQuery(query, {
    ...options,
    // Although the added `customerId` variable will be returned, there's no need to indicate
    // its addition to the return type. We cast the variables type to the generic parameter
    // instead to maintain expected return type.
    variables: {
      ...(options && options.variables),
      customerId,
    } as unknown as TVariables,
    context: {
      ...(options && options.context),
      uri: process.env.GATSBY_CAMEL_URL,
      token: accessToken,
    },
    skip: !!options?.skip || !accessToken || !customerId,
  })

  if (!accessToken) {
    return {
      ...useQueryResult,
      loading: true,
    }
  }

  return useQueryResult
}

export default useCamelQuery
